import { FC, ReactNode } from 'react';
import { css, styled } from '@robocorp/theme';
import { Badge, BadgeProps, Popover, PopoverProps } from '@robocorp/components';
import { IconType } from '@robocorp/icons';
import { IconChevronUp, IconChevronDown } from '@robocorp/icons/iconic';
import { CellPlaceholder } from './Placeholder';

const activeCss = css`
  & > div:last-child {
    color: ${({ theme }) => theme.colors.content.inversed.color};
    &::before {
      background: ${({ theme }) => theme.colors.background.inversed.color};
    }
  }
`;

const Content = styled(Badge)<{ active: boolean }>`
  background: transparent;
  padding-left: 0;

  > span {
    font-weight: inherit;
  }

  & > div:last-child {
    margin-left: ${({ theme }) => theme.space.$8};
  }

  &:hover {
    box-shadow: none;
  }

  ${({ active }) => active && activeCss}
`;

interface BaseProps extends PopoverProps {
  text: ReactNode;
  children: ReactNode;
  icon?: IconType;
}

export const CellTextPopover: FC<BaseProps> = ({
  text,
  children: label,
  icon: Icon = IconChevronDown,
  ...tooltipProps
}) => (
  <Popover
    {...tooltipProps}
    // eslint-disable-next-line react/no-unstable-nested-components
    trigger={({ toggle, referenceRef, referenceProps }) => {
      const isActive = referenceProps['aria-expanded'] === 'true';

      return (
        <Content
          iconAfter={Icon === IconChevronDown && isActive ? IconChevronUp : Icon}
          label={label || <CellPlaceholder />}
          active={isActive}
          iconVisible={isActive}
          forwardedAs="button"
          onClick={toggle}
          ref={referenceRef}
          ghost
          {...referenceProps}
        />
      );
    }}
  >
    {text}
  </Popover>
);

interface Props extends Omit<BaseProps, 'icon'> {
  icon?: BadgeProps['icon'];
  iconAfter?: BadgeProps['iconAfter'];
  iconColor?: BadgeProps['iconColor'];
  variant?: BadgeProps['variant'];
  ghost?: BadgeProps['ghost'];
}

export const CellPopover: FC<Props> = ({
  text,
  children: label,
  icon,
  iconAfter = IconChevronDown,
  iconColor,
  variant,
  ghost,
  ...tooltipProps
}) => (
  <Popover
    {...tooltipProps}
    // eslint-disable-next-line react/no-unstable-nested-components
    trigger={({ toggle, referenceRef, referenceProps }) => {
      const isActive = referenceProps['aria-expanded'] === 'true';

      return (
        <Badge
          icon={icon}
          iconAfter={iconAfter === IconChevronDown && isActive ? IconChevronUp : iconAfter}
          iconVisible={isActive}
          label={label || <CellPlaceholder />}
          iconColor={iconColor}
          variant={variant}
          ghost={ghost}
          forwardedAs="button"
          onClick={toggle}
          ref={referenceRef}
          {...referenceProps}
        />
      );
    }}
  >
    {text}
  </Popover>
);
